import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, Check, Megaphone, Users } from 'lucide-react'
import { campaignsApi, type Campaign, type CampaignStatus } from '@/services/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

interface SelectCampaignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyIds: string[]
  onSuccess?: () => void
}

const STATUS_CONFIG: Record<
  CampaignStatus,
  { label: string; color: string; darkColor: string }
> = {
  draft: {
    label: 'Draft',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    darkColor: 'dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700',
  },
  active: {
    label: 'Active',
    color: 'bg-green-100 text-green-700 border-green-200',
    darkColor: 'dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
  },
  paused: {
    label: 'Paused',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    darkColor: 'dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  },
  completed: {
    label: 'Completed',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    darkColor: 'dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
  },
}

export function SelectCampaignDialog({
  open,
  onOpenChange,
  companyIds,
  onSuccess,
}: SelectCampaignDialogProps) {
  const queryClient = useQueryClient()
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)

  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsApi.list({ page: 1, per_page: 50 }),
    enabled: open,
  })

  const addMutation = useMutation({
    mutationFn: ({ campaignId, companyIds }: { campaignId: string; companyIds: string[] }) =>
      campaignsApi.addCompanies(campaignId, companyIds),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['campaign-companies', campaignId] })
      setSelectedCampaignId(null)
      onOpenChange(false)
      onSuccess?.()
    },
  })

  const campaigns: Campaign[] = campaignsData?.items ?? []

  const handleAdd = () => {
    if (selectedCampaignId && companyIds.length > 0) {
      addMutation.mutate({ campaignId: selectedCampaignId, companyIds })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add to Campaign</DialogTitle>
          <DialogDescription>
            Select a campaign to add {companyIds.length} compan{companyIds.length === 1 ? 'y' : 'ies'} to.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[300px] rounded-md border">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="py-8 text-center">
              <Megaphone className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No campaigns yet</p>
              <p className="text-sm text-muted-foreground">
                Create a campaign first in the Campaigns page
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {campaigns.map((campaign) => {
                const isSelected = selectedCampaignId === campaign.id
                const statusConfig = STATUS_CONFIG[campaign.status]
                return (
                  <div
                    key={campaign.id}
                    className={cn(
                      'flex items-center gap-3 rounded-md p-3 cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-primary/10 border border-primary'
                        : 'hover:bg-muted border border-transparent'
                    )}
                    onClick={() => setSelectedCampaignId(campaign.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{campaign.name}</span>
                        <Badge
                          variant="outline"
                          className={cn('text-xs', statusConfig.color, statusConfig.darkColor)}
                        >
                          {statusConfig.label}
                        </Badge>
                      </div>
                      {campaign.description && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {campaign.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {campaign.stats.total_companies} companies
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="h-5 w-5 text-primary shrink-0" />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!selectedCampaignId || companyIds.length === 0 || addMutation.isPending}
          >
            {addMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Add to Campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
